import { MESSAGE_PROTOCOL_VERSION, bridgeMessageSchema, type ActionRecord, type BridgeMessage, type Message, type SalesforceContext } from "@ai-sales/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type SessionChannel } from "./api.js";
import { createClientId } from "./client-id.js";

const createNonce = () => `${createClientId()}-${Date.now()}`;
const actionPreviewTimeoutMs = 20_000;
type WithoutEnvelope<T> = T extends unknown ? Omit<T, "version" | "requestId" | "nonce"> : never;
type OutboundBridgeMessage = WithoutEnvelope<BridgeMessage>;
type PendingPreview = { action: ActionRecord; timeoutId: number };

export function useSalesforceBridge() {
  const nonce = useMemo(createNonce, []);
  const [context, setContext] = useState<Partial<SalesforceContext>>({});
  const [connected, setConnected] = useState(false);
  const [sessionChannel, setSessionChannel] = useState<SessionChannel>();
  const [consentRequired, setConsentRequired] = useState(false);
  const [actionCompletion, setActionCompletion] = useState<{
    action: ActionRecord;
    relatedActions?: ActionRecord[];
    assistantMessage: Message;
    context?: Partial<SalesforceContext>;
  }>();
  const [previewCompletion, setPreviewCompletion] = useState<ActionRecord>();
  const [previewFailure, setPreviewFailure] = useState<{ actionId: string; error: string }>();
  const previewRequests = useMemo(() => new Map<string, PendingPreview>(), []);
  const parentOrigin = new URLSearchParams(window.location.search).get("parentOrigin");

  const send = useCallback((message: OutboundBridgeMessage) => {
    if (!parentOrigin || window.parent === window) return;
    window.parent.postMessage({
      ...message,
      version: MESSAGE_PROTOCOL_VERSION,
      requestId: createClientId(),
      nonce
    }, parentOrigin);
  }, [nonce, parentOrigin]);

  useEffect(() => {
    const handler = (event: MessageEvent<unknown>) => {
      if (!parentOrigin || event.origin !== parentOrigin || event.source !== window.parent) return;
      const parsed = bridgeMessageSchema.safeParse(event.data);
      if (!parsed.success || parsed.data.nonce !== nonce) return;
      if (parsed.data.type === "salesforce.session.ticket") {
        void api.exchange(parsed.data.ticket, nonce).then((result) => {
          setContext((current) => ({ ...current, ...result.context }));
          setSessionChannel(result.channel);
          setConsentRequired(Boolean(result.consentRequired));
          setConnected(true);
        });
      }
      if (parsed.data.type === "salesforce.context.changed") {
        const nextContext = parsed.data.context;
        setContext((current) => ({ ...current, ...nextContext }));
      }
      if (parsed.data.type === "salesforce.action.result") {
        const actionId = parsed.data.actionId;
        const pendingPreview = previewRequests.get(actionId);
        if (pendingPreview) {
          window.clearTimeout(pendingPreview.timeoutId);
          previewRequests.delete(actionId);
          void api.reportActionPreview(pendingPreview.action, {
            ok: parsed.data.ok,
            ...(parsed.data.result !== undefined ? { result: parsed.data.result } : {}),
            ...(parsed.data.error ? { error: parsed.data.error } : {})
          }).then(setPreviewCompletion).catch((cause) => setPreviewFailure({
            actionId,
            error: cause instanceof Error ? cause.message : "预览核对失败"
          }));
          return;
        }
        void api.reportActionResult(parsed.data.actionId, {
          ok: parsed.data.ok,
          ...(parsed.data.result !== undefined ? { result: parsed.data.result } : {}),
          ...(parsed.data.error ? { error: parsed.data.error } : {})
        }).then(({ action, relatedActions, assistantMessage, context }) => setActionCompletion({
          action,
          ...(relatedActions?.length ? { relatedActions } : {}),
          assistantMessage,
          ...(context ? { context } : {})
        }));
      }
      if (parsed.data.type === "salesforce.action.preview.result") {
        const actionId = parsed.data.actionId;
        const pendingAction = previewRequests.get(actionId);
        if (!pendingAction) return;
        window.clearTimeout(pendingAction.timeoutId);
        previewRequests.delete(actionId);
        void api.reportActionPreview(pendingAction.action, {
          ok: parsed.data.ok,
          ...(parsed.data.result !== undefined ? { result: parsed.data.result } : {}),
          ...(parsed.data.error ? { error: parsed.data.error } : {})
        }).then(setPreviewCompletion).catch((cause) => setPreviewFailure({
          actionId,
          error: cause instanceof Error ? cause.message : "预览核对失败"
        }));
      }
    };
    window.addEventListener("message", handler);
    const announceReady = () => send({ type: "agent.ready" });
    let retryTimer: number | undefined;
    if (!connected) {
      announceReady();
      retryTimer = window.setInterval(announceReady, 1500);
    }
    return () => {
      window.removeEventListener("message", handler);
      if (retryTimer !== undefined) window.clearInterval(retryTimer);
    };
  }, [connected, nonce, parentOrigin, previewRequests, send]);

  const executeAction = useCallback(async (action: ActionRecord) => {
    await api.claimActionExecution(action.id);
    send({ type: "salesforce.action.request", actionId: action.id, plan: action.plan });
  }, [send]);

  const previewAction = useCallback(async (action: ActionRecord) => {
    await api.authorizeActionPreview(action);
    const previous = previewRequests.get(action.id);
    if (previous) window.clearTimeout(previous.timeoutId);
    const timeoutId = window.setTimeout(() => {
      const pending = previewRequests.get(action.id);
      if (!pending || pending.action.contentHash !== action.contentHash) return;
      previewRequests.delete(action.id);
      setPreviewFailure({
        actionId: action.id,
        error: "Salesforce 记录核对超时，请检查连接后重新核对。"
      });
    }, actionPreviewTimeoutMs);
    previewRequests.set(action.id, { action, timeoutId });
    send({ type: "salesforce.action.preview.request", actionId: action.id, plan: action.plan });
  }, [previewRequests, send]);

  useEffect(() => () => {
    for (const pending of previewRequests.values()) window.clearTimeout(pending.timeoutId);
    previewRequests.clear();
  }, [previewRequests]);

  return {
    nonce,
    context,
    connected,
    sessionChannel,
    consentRequired,
    setConsentRequired,
    setConnected,
    setContext,
    executeAction,
    previewAction,
    send,
    actionCompletion,
    setActionCompletion,
    previewCompletion,
    setPreviewCompletion,
    previewFailure,
    setPreviewFailure
  };
}
